import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from 'react';

import useSWR from 'swr';
import io from 'socket.io-client';
import { parseCookies } from 'nookies';
import { useToast } from '@chakra-ui/react';

import { getSegmentFromCookies } from '~/shared/utils/getSegmentFromCookies';
import { FCWithChildren } from '~/shared/types/FCWithChildren';
import { api } from '~/shared/services/api';
import { IPagination } from '~/shared/interfaces/IPagination';
import { env } from '~/shared/constants/env';
import { cookies } from '~/shared/constants/cookies';
import { useAuth } from '~/modules/auth/hooks/auth';

import { INotification } from '../interfaces/INotifications';

interface INotificationsContextData {
  notifications: INotification[];
  totalCount: number;
  read(id: string): void;
  readAll(): void;
}

const NotificationsContext = createContext({} as INotificationsContextData);

const socket = io(`${env.API_URI}/notifications`, {
  transports: ['websocket'],
});

const NotificationsProvider: FCWithChildren = ({ children }) => {
  const segment = getSegmentFromCookies();
  const { user } = useAuth();
  const toast = useToast({
    isClosable: true,
    status: 'error',
    position: 'top-right',
  });

  const { data, mutate } = useSWR<IPagination<INotification>>(
    `/segments/${segment}/notifications?read=false&limit=20&page=1`
  );

  const userCompanyIds = useMemo(() => {
    const companiesFromUser =
      user?.access?.[0]?.companies?.map((company) => company._id) || [];

    if (companiesFromUser.length > 0) return companiesFromUser;

    const parsedCookies = parseCookies();

    return (parsedCookies[cookies.USER_COMPANIES] || '')
      .split('|')
      .filter(Boolean);
  }, [user]);

  const userCompanyIdsSet = useMemo(
    () => new Set(userCompanyIds),
    [userCompanyIds]
  );

  const notifications = useMemo(() => data?.data || [], [data]);

  const totalCount = useMemo(() => data?.totalCount || 0, [data]);

  const newNotificationsListener = useCallback(
    (notification: INotification): void => {
      if (!userCompanyIdsSet.has(notification.company)) return;

      mutate((old) => {
        return {
          ...old,
          data: [notification, ...(old?.data || [])],
          totalCount: (old?.totalCount || 0) + 1,
        };
      }, false);

      new Audio('/notification.mp3').play().catch(() => null);
    },
    [mutate, userCompanyIdsSet]
  );

  useEffect(() => {
    socket.on(`new-notification-${segment}`, newNotificationsListener);

    return () => {
      socket.off(`new-notification-${segment}`, newNotificationsListener);
    };
  }, [newNotificationsListener, segment]);

  const read = useCallback(
    (id: string) => {
      let oldData: IPagination<INotification>;

      mutate((old) => {
        if (old) oldData = old;

        return {
          ...old,
          data: old?.data?.filter((item) => item._id !== id),
          totalCount: old?.totalCount - 1,
        };
      }, false);

      mutate(async (old) => {
        try {
          await api.patch(`/segments/${segment}/notifications/${id}/read`);

          return old;
        } catch {
          toast({
            title: 'Ops, ocorreu um erro ao limpar a notificação!',
            description: 'Recarregue a página e tente novamente.',
          });

          return oldData;
        }
      }, oldData?.data?.length === 1);
    },
    [mutate, segment, toast]
  );

  const readAll = useCallback(() => {
    let oldData: IPagination<INotification>;

    mutate((old) => {
      if (old) oldData = old;

      return {
        data: [],
        totalCount: 0,
        page: 1,
        limit: 20,
        pagesCount: 0,
      };
    }, false);

    mutate(async () => {
      try {
        await api.patch(`/segments/${segment}/notifications/read-all`);

        return {
          data: [],
          totalCount: 0,
          page: 1,
          limit: 20,
          pagesCount: 0,
        };
      } catch {
        toast({
          title: 'Ops, ocorreu um erro ao limpar todas as notificações!',
          description: 'Recarregue a página e tente novamente.',
        });

        return oldData;
      }
    });
  }, [mutate, segment, toast]);

  return (
    <NotificationsContext.Provider
      value={{ notifications, readAll, read, totalCount }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

const useNotifications = (): INotificationsContextData => {
  return useContext(NotificationsContext);
};

export { NotificationsProvider, useNotifications };
